import {
  Box,
  BoxProps,
  Grid,
  HStack,
  Stack,
  Text,
  forwardRef,
} from "@chakra-ui/react"
import { DangerFilledIcon } from "Icons/DangerFilledIcon"
import React, { Children, PropsWithChildren, ReactNode } from "react"

type BaseItemProps = {
  label: ReactNode
  icon?: ReactNode
  error?: ReactNode
  isSelected?: boolean
  isDisabled?: boolean
  onClick?: () => void
}

export type ItemProps = Omit<BoxProps, keyof BaseItemProps> & BaseItemProps

export const Item = forwardRef<ItemProps, "button">(
  (
    {
      label,
      icon,
      isSelected,
      isDisabled,
      error,
      children,
      onClick,
      ...props
    }: PropsWithChildren<ItemProps>,
    ref
  ) => {
    const rows = +(Children.count(children) > 0) + +!!error
    return (
      <Grid
        ref={ref}
        as="button"
        role="menuitem"
        position="relative"
        templateColumns="1.5rem 1fr"
        templateRows={`repeat(${rows}, auto)`}
        rowGap={1}
        columnGap={3}
        p={3}
        rounded="0.5rem"
        alignItems="start"
        textAlign="left"
        aria-disabled={isDisabled || undefined}
        aria-selected={isSelected || undefined}
        onClick={onClick}
        _hover={{
          backgroundColor: "ds.background.neutral.subtle.hovered",
        }}
        _focusVisible={{
          outline: "none",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            boxShadow: "0 0 0 4px var(--chakra-colors-ds-border-focused)",
            pointerEvents: "none",
            opacity: 0.5,
            borderRadius: "inherit",
          },
        }}
        sx={{
          "&[aria-selected='true']": {
            backgroundColor: "ds.background.neutral.resting",
            backgroundImage:
              "linear-gradient(to right, var(--chakra-colors-ds-icon-selected) 0.25rem, transparent 0)",

            "[data-icon] .chakra-icon": {
              color: "ds.icon.selected",
            },
          },

          "&[aria-disabled='true']": {
            "[data-icon] .chakra-icon": {
              color: "ds.icon.disabled",
            },
          },
        }}
        {...props}
      >
        <Box
          as="span"
          gridColumn="1"
          gridRow="1"
          data-icon
          sx={{
            ".chakra-icon": {
              display: "block",
              boxSize: 6,
              color: "ds.icon.subtle",
            },
          }}
        >
          {icon}
        </Box>
        <Box
          gridColumn="2"
          textStyle="ds.interface.large"
          color={
            isDisabled
              ? "ds.text.disabled"
              : isSelected
                ? "ds.text.selected"
                : "ds.text.default"
          }
          my="calc((1.5rem - 1lh) / 2)"
        >
          {label}
        </Box>
        {children && (
          <Stack
            gridColumn="2"
            textStyle="ds.paragraph.primary"
            color={isDisabled ? "ds.text.disabled" : "ds.text.subtle"}
            gap={1}
          >
            {children}
          </Stack>
        )}
        {error && (
          <HStack gridColumn="2" align="start">
            <DangerFilledIcon color="ds.icon.danger" />
            <Text
              as="span"
              color="ds.text.danger"
              textStyle="ds.paragraph.secondary"
            >
              {error}
            </Text>
          </HStack>
        )}
      </Grid>
    )
  }
)
